import React, { useEffect, useState } from "react";
import "../components/CookieConsent.css";
import i18n from "../i18n";

const COOKIE_KEY = "cookie_consent";
const LANG_KEY = "preferredLang";
const API = process.env.REACT_APP_API_URL || "https://security-xi-rust.vercel.app";

function getDeviceType() {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "tablet";
  if (/mobile|iphone|ipod|android|blackberry|mini|windows\sce|palm/i.test(ua)) return "mobile";
  return "desktop";
}

function getBrowserName() {
  const ua = navigator.userAgent;
  if (ua.includes("Edg")) return "Edge";
  if (ua.includes("OPR") || ua.includes("Opera")) return "Opera";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Safari")) return "Safari";
  return "Other";
}

function trackEvent(eventType, lang) {
  try {
    let sid = sessionStorage.getItem("analytics_session_id");
    if (!sid) {
      sid = Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem("analytics_session_id", sid);
    }
    fetch(`${API}/api/analytics/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type: eventType,
        page: window.location.pathname,
        referrer: document.referrer || null,
        lang: lang || "en",
        device_type: getDeviceType(),
        browser: getBrowserName(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        session_id: sid,
      }),
    }).catch(() => {});
  } catch (e) {
    // silently ignore — never interrupt user experience
  }
}

// Google Consent Mode v2. public/index.html sets the defaults (all denied) before
// the Google Ads tag loads and re-applies a stored "accepted" choice
// (localStorage cookie_consent === "true") on later page loads.
function updateGoogleConsent(granted) {
  try {
    if (typeof window.gtag !== "function") return;
    const v = granted ? "granted" : "denied";
    window.gtag("consent", "update", {
      ad_storage: v,
      ad_user_data: v,
      ad_personalization: v,
      analytics_storage: v,
    });
  } catch (e) {
    // never interrupt the user experience
  }
}

function setCookie(name, value, days = 365) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};path=/;expires=${d.toUTCString()};SameSite=Lax`;
}

function getCookie(name) {
  const matches = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return matches ? matches[2] : null;
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [consent, setConsent] = useState(() => {
    const c = localStorage.getItem(COOKIE_KEY);
    if (c !== null) return c === "true";
    const ck = getCookie(COOKIE_KEY);
    if (ck !== null) return ck === "true";
    return null;
  });
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || i18n.language || "en");

  useEffect(() => {
    // Skip during prerender so prerendered HTML matches client's first render
    // (avoids React #418/#423 hydration mismatch — banner DOM otherwise lands
    // in the static HTML but client first paints null).
    if (typeof navigator !== "undefined" && navigator.userAgent.includes("HeadlessChrome")) return;
    // show banner only when consent not recorded
    if (consent === null) setVisible(true);
    else setVisible(false);
  }, [consent]);

  useEffect(() => {
    // ensure i18n reflects stored preference on mount
    if (lang && i18n.language !== lang) i18n.changeLanguage(lang);
  }, []); // eslint-disable-line

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showDataModal) {
        setShowDataModal(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showDataModal]);

  function acceptAll() {
    localStorage.setItem(COOKIE_KEY, "true");
    setCookie(COOKIE_KEY, "true", 365);
    updateGoogleConsent(true);
    setConsent(true);
    setVisible(false);
    setShowDataModal(false);
    trackEvent("consent_accept", lang);
  }

  function decline() {
    localStorage.setItem(COOKIE_KEY, "false");
    updateGoogleConsent(false);
    setConsent(false);
    setVisible(false);
    setShowDataModal(false);
    trackEvent("consent_decline", lang);
  }

  function chooseLang(l) {
    setLang(l);
    localStorage.setItem(LANG_KEY, l);
    i18n.changeLanguage(l);
  }

  const getContent = () => {
    if (lang === 'fr') {
      return {
        title: "Nous utilisons des cookies",
        description: "En continuant, vous acceptez notre utilisation des cookies. Choisissez votre préférence de langue ci-dessous.",
        learnMore: "En savoir plus",
        decline: "Refuser",
        accept: "Accepter",
        modalTitle: "Comment nous utilisons vos données",
        modalSections: {
          essential: {
            title: "🔒 Cookies essentiels",
            content: "Ces cookies sont nécessaires au fonctionnement de notre site web. Ils permettent des fonctionnalités de base comme la navigation dans les pages et l'accès aux zones sécurisées."
          },
          functional: {
            title: "⚙️ Cookies fonctionnels", 
            content: "Ces cookies nous permettent de mémoriser vos préférences (comme la langue) et de personnaliser votre expérience sur notre site."
          },
          analytics: {
            title: "📊 Cookies d'analyse et de publicité",
            content: "Avec votre accord, nous mesurons de façon anonyme les pages visitées sur notre site, et Google Ads mesure l'efficacité de nos publicités (par exemple, si une visite issue d'une annonce mène à une demande de soumission ou à un appel). Si vous refusez, aucun cookie publicitaire n'est enregistré."
          },
          dataUse: {
            title: "🛡️ Comment nous protégeons vos données",
            content: "Nous ne vendons jamais vos données personnelles. Toutes les informations collectées sont utilisées uniquement pour améliorer nos services et votre expérience utilisateur. Vos données sont stockées de manière sécurisée et conformément aux lois canadiennes sur la protection de la vie privée."
          },
          retention: {
            title: "⏰ Conservation des données",
            content: "Les cookies de préférences sont conservés pendant 1 an. Les cookies de mesure publicitaire de Google Ads sont conservés selon les politiques de Google (généralement 90 jours au maximum)."
          }
        },
        close: "Fermer"
      };
    }
    return {
      title: "We use cookies",
      description: "By continuing you agree to our use of cookies. Choose your language preference below.",
      learnMore: "Learn More",
      decline: "Decline",
      accept: "Accept",
      modalTitle: "How We Use Your Data",
      modalSections: {
        essential: {
          title: "🔒 Essential Cookies",
          content: "These cookies are necessary for our website to function. They enable basic features like page navigation and access to secure areas."
        },
        functional: {
          title: "⚙️ Functional Cookies",
          content: "These cookies allow us to remember your preferences (like language selection) and personalize your experience on our site."
        },
        analytics: {
          title: "📊 Analytics & Advertising Cookies",
          content: "With your consent, we anonymously measure which pages are visited on our site, and Google Ads measures how well our ads work (for example, whether a visit from an ad leads to a quote request or a call). If you decline, no advertising cookies are stored."
        },
        dataUse: {
          title: "🛡️ How We Protect Your Data",
          content: "We never sell your personal data. All information collected is used solely to improve our services and your user experience. Your data is stored securely and in compliance with Canadian privacy laws."
        },
        retention: {
          title: "⏰ Data Retention",
          content: "Preference cookies are kept for 1 year. Google Ads measurement cookies are kept according to Google's policies (typically up to 90 days)."
        }
      },
      close: "Close"
    };
  };

  const content = getContent();

  if (!visible) return null;

  return (
    <>
      <div className="cookie-consent-backdrop" />
      <div className="cookie-consent" role="dialog" aria-live="polite" aria-label="Cookie preferences">
        <div className="cookie-consent__inner">
          <div className="cookie-consent__header">
            <div className="cookie-consent__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div className="cookie-consent__title-group">
              <p className="cookie-consent__title">{content.title}</p>
            </div>
            <div className="cookie-consent__lang">
              <button
                className={`lang-btn ${lang === "en" ? "is-active" : ""}`}
                onClick={() => chooseLang("en")}
                aria-pressed={lang === "en"}
                type="button"
              >
                EN
              </button>
              <button
                className={`lang-btn ${lang === "fr" ? "is-active" : ""}`}
                onClick={() => chooseLang("fr")}
                aria-pressed={lang === "fr"}
                type="button"
              >
                FR
              </button>
            </div>
          </div>

          <div className="cookie-consent__text">
            <p className="cookie-consent__desc">
              {content.description}
            </p>
          </div>

          <hr className="cookie-consent__divider" />

          <div className="cookie-consent__controls">
            <button 
              className="btn btn-learn-more" 
              onClick={() => setShowDataModal(true)} 
              type="button"
            >
              {content.learnMore}
            </button>
            <div className="cookie-consent__actions">
              <button className="btn btn-ghost" onClick={decline} type="button">
                {content.decline}
              </button>
              <button className="btn btn-primary" onClick={acceptAll} type="button">
                {content.accept}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Data Usage Modal */}
      {showDataModal && (
        <div 
          className="data-modal-overlay" 
          onClick={() => setShowDataModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="data-modal-title"
        >
          <div 
            className="data-modal-content" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="data-modal-header">
              <h2 id="data-modal-title">{content.modalTitle}</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowDataModal(false)}
                aria-label="Close modal"
                type="button"
              >
                ×
              </button>
            </div>
            
            <div className="data-modal-body">
              <div className="data-section">
                <h3>{content.modalSections.essential.title}</h3>
                <p>{content.modalSections.essential.content}</p>
              </div>

              <div className="data-section">
                <h3>{content.modalSections.functional.title}</h3>
                <p>{content.modalSections.functional.content}</p>
              </div>

              <div className="data-section">
                <h3>{content.modalSections.analytics.title}</h3>
                <p>{content.modalSections.analytics.content}</p>
              </div>

              <div className="data-section">
                <h3>{content.modalSections.dataUse.title}</h3>
                <p>{content.modalSections.dataUse.content}</p>
              </div>

              <div className="data-section">
                <h3>{content.modalSections.retention.title}</h3>
                <p>{content.modalSections.retention.content}</p>
              </div>
            </div>

            <div className="data-modal-footer">
              <button className="btn btn-ghost" onClick={decline} type="button">
                {content.decline}
              </button>
              <button className="btn btn-primary" onClick={acceptAll} type="button">
                {content.accept}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}